import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AbortJobModal from "../Jobs/AbortJobModal";
import DeletionStatusList from "../Jobs/DeletionStatusList";
import { isJobFinished } from "../../models/Jobs/JobActions";
import JobsRepository from "../../repositories/JobsRepository";
import { JobRequestStatus } from "../../models/Jobs/JobRequestStatus";
import { DeleteFirmDocumentsRequest, JobRequest } from "../../models/Jobs/JobRequest";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { QUERY_CONFIG } from "../../constants/QueryConfig";

interface DeleteFirmDocumentsStatusTableProps {
  firmDocumentsFolderId?: string;
}

export const DeleteFirmDocumentsStatusTable: React.FC<DeleteFirmDocumentsStatusTableProps> = ({
  firmDocumentsFolderId,
}) => {
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [abortJob, setAbortJob] = useState<JobRequest | null>(null);
  const [firmDocumentsDeletionStatus, setFirmDocumentsDeletionStatus] = useState<DeleteFirmDocumentsRequest[] | null>(null);

  const statusQuery = useQuery({
    queryKey: [QUERY_KEYS.DELETE_FIRM_DOCUMENTS, firmDocumentsFolderId],
    queryFn: async () => await jobsRepository.getFirmDocumentsDeletionStatus(firmDocumentsFolderId),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    refetchInterval: (query) =>
      query.state.data?.some((job) => !isJobFinished(job)) ? 30_000 : QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
    refetchIntervalInBackground: true,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (statusQuery.data) setFirmDocumentsDeletionStatus(statusQuery.data);
  }, [statusQuery]);

  const notifiedJobIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!statusQuery.data) return;
    const terminal = new Set([JobRequestStatus.Completed, JobRequestStatus.Failed]);
    const newlyTerminal = statusQuery.data.filter(
      (j) => terminal.has(j.status) && !notifiedJobIdsRef.current.has(j.id),
    );
    if (newlyTerminal.length === 0) return;
    newlyTerminal.forEach((j) => notifiedJobIdsRef.current.add(j.id));
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS, firmDocumentsFolderId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS_FOLDER, firmDocumentsFolderId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS_FOLDERS] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION] });
  }, [statusQuery.data, firmDocumentsFolderId, queryClient]);

  return (
    <>
      <DeletionStatusList
        jobs={firmDocumentsDeletionStatus ?? []}
        loading={statusQuery.isFetching || statusQuery.isError}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onAbort={setAbortJob}
        onStopped={() => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DELETE_FIRM_DOCUMENTS, firmDocumentsFolderId] })}
      />
      {abortJob && (
        <AbortJobModal
          open={abortJob !== null}
          jobRequest={abortJob}
          onClose={() => setAbortJob(null)}
          onConfirm={async () => {
            await jobsRepository.deleteJob(abortJob.id);
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DELETE_FIRM_DOCUMENTS, firmDocumentsFolderId] });
            if (abortJob.status !== JobRequestStatus.Completed) {
              await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS, firmDocumentsFolderId] });
              await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS_FOLDER, firmDocumentsFolderId] });
              await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS_FOLDERS] });
              await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION] });
            }
            setAbortJob(null);
          }}
        />
      )}
    </>
  );
};
